import * as React from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  FormHelperText,
  InputBase,
  MenuItem,
  Select,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { identity } from "../../util";
import clsx from "clsx";

function ifFancy(a, b) {
  return ({ fancy }) => (fancy ? a : b);
}

const SmallInput = withStyles((theme) => ({
  root: {
    background: theme.palette.background.paper,
    boxShadow: ifFancy(theme.boxShadow.tight, "none"),
    borderRadius: theme.shape.borderRadius,
    minWidth: 116,
  },
  input: {
    "&.MuiSelect-selectMenu": {
      height: ifFancy(undefined, "25px"),
      alignItems: ifFancy(undefined, "center"),
      display: ifFancy(undefined, "flex"),
    },
    fontSize: ifFancy(
      theme.typography.pxToRem(13),
      theme.typography.body2.fontSize,
    ),
    fontWeight: ifFancy(theme.typography.medium, theme.typography.regular),
    padding: ifFancy("3px 8px", "5px 8px"),
    "&.notDefault": {
      background: theme.gradient.text,
      backgroundClip: "text",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      textFillColor: "transparent",
      fontWeight: theme.typography.regular,
    },
  },
}))(InputBase);

const StyledSelect = withStyles((theme) => ({
  root: {
    border: ifFancy("2px solid transparent", "1px solid rgb(197 197 197)"),
    borderRadius: theme.shape.borderRadius,
    "&:focus": {
      borderRadius: theme.shape.borderRadius,
      borderColor: theme.palette.grey[800],
    },
  },
}))(Select);

const StyledMenuItem = withStyles((theme) => ({
  root: {
    fontFamily: theme.typography.interFontStack,
    fontWeight: theme.typography.medium,
    fontSize: theme.typography.body2.fontSize,
    background: theme.palette.background.paper,
    padding: "10px 13px",
  },
}))(MenuItem);

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: 4,
    borderRadius: theme.shape.md,
    border: `1px solid ${theme.palette.grey[300]}`,
    background: theme.palette.background.paper,
    maxHeight: "24rem",
  },
  list: {
    display: "flex",
    flexFlow: "column nowrap",
    background: theme.palette.background.surfaceContainer,
    gap: 2,
    minWidth: 140,
    padding: 0,
    "& .Mui-selected": {
      background: theme.palette.background.surfaceContainerLow,
    },
    "& .MuiListItem-root:hover": {
      background: theme.palette.background.surfaceContainerLow,
    },
  },
}));

const FancySelect = React.memo(function FancySelect({
  options = [],
  onChange,
  disabled,
  value,
  getOptionLabel = identity,
  getOptionValue = identity,
  fancy,
  helperText,
  error,
  formControlProps,
  menuClassName = "",
  MenuItemComponent = StyledMenuItem,
  ...props
}) {
  const classes = useStyles();
  const [firstOption] = options;

  return (
    <FormControl error={Boolean(error)} {...formControlProps}>
      <StyledSelect
        {...props}
        fancy={fancy ? String(fancy) : undefined}
        input={<SmallInput />}
        inputProps={{
          className: clsx(
            fancy &&
              !disabled &&
              value !== getOptionValue(firstOption) &&
              "notDefault",
          ),
        }}
        value={value}
        onChange={onChange}
        disabled={disabled}
        MenuProps={{
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          getContentAnchorEl: null,
          classes,
        }}
      >
        {options.map((option) => (
          <MenuItemComponent
            key={getOptionValue(option)}
            value={getOptionValue(option)}
            className={menuClassName}
          >
            {getOptionLabel(option)}
          </MenuItemComponent>
        ))}
      </StyledSelect>

      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
});

FancySelect.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  menuClassName: PropTypes.string,
  MenuItemComponent: PropTypes.elementType,
};

export default FancySelect;
